import { useEffect, useState } from "react";
import { api, flash, patch, post } from "../lib/api";
import { Sheet } from "./bits";

const SWATCHES = Array.from({ length: 12 }, (_, i) => (i * 30 + 4) % 360);
const TYPES = [["retainer", "Retainer", "Ongoing, month after month"], ["project", "Project", "One job, start to finish"], ["internal", "Internal", "CK's own work"]];
const gap = (a, b) => { const d = Math.abs(a - b) % 360; return Math.min(d, 360 - d); };
const nearest = (h, list) => Math.min(360, ...list.map((c) => gap(h, c.hue ?? 0)));

function freestHue(all) {
  const ret = all.filter((c) => c.type === "retainer"), rest = all.filter((c) => c.type !== "internal");
  return [...SWATCHES].sort((a, b) => nearest(b, ret) - nearest(a, ret) || nearest(b, rest) - nearest(a, rest))[0];
}

function problems(f, all, editing) {
  const e = {};
  const others = all.filter((c) => c.code !== editing?.code);
  if (!f.name.trim()) e.name = "Give the client a name.";
  else if (others.some((c) => c.name.toLowerCase() === f.name.trim().toLowerCase())) e.name = "A client with this name already exists.";
  if (!editing) {
    const taken = all.find((c) => c.code === f.code);
    if (!/^[A-Z]{2,4}$/.test(f.code)) e.code = "Use 2 to 4 letters, like ZOM.";
    else if (f.code === "TRUE") e.code = "TRUE cannot be a code. Pick other letters.";
    else if (taken) e.code = `${f.code} is taken by ${taken.name}.`;
  }
  return e;
}

function OwnerPicks({ value, people, onChange }) {
  const pool = people.filter((p) => p.active && !p.leftAt);
  const groups = [["Client servicing", pool.filter((p) => p.team === "servicing")], ["Social media", pool.filter((p) => p.team === "social")],
    ["Management", pool.filter((p) => p.role === "management" && !["servicing", "social"].includes(p.team))]];
  const shown = new Set(groups.flatMap(([, l]) => l.map((p) => p.id)));
  const kept = value.filter((id) => !shown.has(id)).map((id) => people.find((p) => p.id === id) || { id, name: id });
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const box = (p) => (
    <label key={p.id} className={`poc-pick ${value.includes(p.id) ? "on" : ""}`} data-testid={`client-poc-${p.id}`}>
      <input type="checkbox" checked={value.includes(p.id)} onChange={() => toggle(p.id)} data-testid={`client-poc-check-${p.id}`} />{p.name}</label>);
  return (
    <div className="poc-picks" role="group" aria-label="Serviced by" data-testid="client-owners">
      {kept.length > 0 && <div className="poc-g"><span className="lbl">Current</span><div>{kept.map(box)}</div></div>}
      {groups.map(([label, list]) => list.length > 0 && <div key={label} className="poc-g"><span className="lbl">{label}</span><div>{list.map(box)}</div></div>)}
    </div>
  );
}

export function ClientForm({ me, client, all, onClose, onDone }) {
  const [people, setPeople] = useState([]);
  const [f, setF] = useState(() => client ? { name: client.name, code: client.code, type: client.type, owners: client.owners || [], hue: client.hue ?? 0 }
    : { name: "", code: "", type: "retainer", owners: ["servicing", "social"].includes(me.user.team) ? [me.user.id] : [], hue: freestHue(all) });
  const [codeTouched, setCodeTouched] = useState(!!client);
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api("/people").then((r) => r.ok && setPeople(r.data.items)); }, []);
  const setName = (name) => setF({ ...f, name, code: codeTouched ? f.code : name.replace(/[^a-z]/gi, "").slice(0, 3).toUpperCase() });
  const close = all.find((c) => c.type === "retainer" && c.code !== client?.code && gap(c.hue ?? 0, f.hue) < 15);
  async function save(e) {
    e.preventDefault();
    const p = problems(f, all, client);
    setErrs(p);
    if (Object.keys(p).length) return document.getElementById(p.name ? "cf-name" : "cf-code")?.focus();
    setBusy(true);
    const body = { name: f.name.trim(), type: f.type, owners: f.owners, hue: f.hue };
    const r = client ? await patch(`/clients/${client.code}`, body) : await post("/clients", { ...body, code: f.code });
    setBusy(false);
    if (!r.ok) {
      const k = r.error.code === "NAME_TAKEN" || /name/i.test(r.error.message) ? "name" : r.error.code === "CODE_TAKEN" || /letters|code/i.test(r.error.message) ? "code" : "form";
      return setErrs({ [k]: r.error.message });
    }
    flash(client ? `Saved ${r.data.client.name}.` : r.data.message);
    onDone(r.data.client, !client);
  }
  const err = (k) => errs[k] && <p className="err" id={`cf-${k}-err`} role="alert" data-testid={`client-${k}-error`}>{errs[k]}</p>;
  return (
    <Sheet title={client ? `Edit ${client.name}` : "Add client"} onClose={onClose} testid="client-form-sheet" width={540}>
      <form onSubmit={save} className="cform">
        <div className="cf-preview"><span className="chip-big" style={{ background: `oklch(0.5 0.13 ${f.hue})` }} data-testid="client-chip-preview">{f.code || "ABC"}</span>
          <div><b>{f.name || "New client"}</b><p className="hint">How the chip looks on cards, the Floor and Clients.</p></div></div>
        <fieldset className="cf-set"><legend className="lbl">Client</legend>
          <label className="fld-lbl" htmlFor="cf-name">Name</label>
          <input id="cf-name" className="fld" autoFocus maxLength={60} placeholder="e.g. Zomato" value={f.name} aria-invalid={!!errs.name}
            aria-describedby={errs.name ? "cf-name-err" : undefined} onChange={(e) => setName(e.target.value)} data-testid="client-name" />
          {err("name")}
          <label className="fld-lbl" htmlFor="cf-code">Code {client && <span className="tag tag-outline" data-testid="client-code-fixed">Fixed</span>}</label>
          <input id="cf-code" className="fld mono" maxLength={4} placeholder="ZOM" value={f.code} readOnly={!!client} aria-invalid={!!errs.code}
            aria-describedby={errs.code ? "cf-code-err" : undefined} onChange={(e) => { setCodeTouched(true); setF({ ...f, code: e.target.value.toUpperCase() }); }} data-testid="client-code" />
          {err("code")}
          <p className="hint">{client ? "Codes never change: every logged hour is filed under this one." : "Three letters is the house style. The code is fixed once saved: every logged hour is filed under it."}</p>
        </fieldset>
        <div className="lbl mt-s">Type</div>
        <div className="seg3" role="radiogroup" aria-label="Type">
          {TYPES.map(([v, l, hint]) => (
            <button type="button" key={v} role="radio" aria-checked={f.type === v} className={f.type === v ? "on" : ""} onClick={() => setF({ ...f, type: v })} data-testid={`client-type-${v}`}>
              <b>{l}</b><span>{hint}</span></button>))}
        </div>
        <div className="fld-lbl">Serviced by <span className="hint0" data-testid="client-owners-count">{f.owners.length ? `${f.owners.length} POC${f.owners.length > 1 ? "s" : ""}` : "nobody yet"}</span></div>
        <OwnerPicks value={f.owners} people={people} onChange={(owners) => setF({ ...f, owners })} />
        <p className="hint">Tick every POC. Shown as "Serviced by" on Clients and in the export. Anyone who plans can still add work for this client.</p>
        <div className="lbl mt-s">Chip colour</div>
        <div className="swatches" role="radiogroup" aria-label="Chip colour">
          {SWATCHES.map((h, i) => {
            const free = nearest(h, all.filter((c) => c.type === "retainer" && c.code !== client?.code)) >= 15;
            return <button type="button" key={h} role="radio" aria-checked={f.hue === h} className={`sw ${f.hue === h ? "on" : ""} ${free ? "free" : ""}`}
              style={{ background: `oklch(0.58 0.15 ${h})` }} aria-label={`Colour ${i + 1} of 12${free ? ", no retainer wears it yet" : ""}`}
              onClick={() => setF({ ...f, hue: h })} data-testid={`client-swatch-${i}`} />;
          })}
        </div>
        <p className="hint">A small square in the corner marks a colour no retainer wears yet. Each retainer should own one so the Floor stays scannable; projects can share.</p>
        {close && <p className="hint" data-testid="client-hue-close">Close to {close.name} ({close.code}), a retainer. Pick another if they share a Floor.</p>}
        {!SWATCHES.includes(f.hue) && <p className="hint">The current colour sits between two swatches. It stays as it is until you pick one.</p>}
        {err("form")}
        <div className="sheet-foot">
          <button type="button" className="btn-ghost sm" onClick={onClose} data-testid="client-form-cancel">Cancel</button>
          <button className="btn-solid" disabled={busy} data-testid="client-form-save">{client ? "Save changes" : "Add client"}</button>
        </div>
      </form>
    </Sheet>
  );
}
